import { useRequest } from 'ahooks'
import { Collapse, CollapsePanelProps, List, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Favorite, ProjectProgress, RiskBar } from '@/components'
import { routes } from '@/router'
import { Portfolio, Project, ResourceType, Team } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

import { Customer } from '../customer/customer.schema'
import { PlanDate } from './plan-date.component'
import { Switch } from './switch.component'

const { Text } = Typography

interface Favorite {
  id: number
  selfId: string
  type: ResourceType
  project: Project
  portfolio: Portfolio
  team: Team
  customer: Customer
}

export const MyFavorites: React.FC<CollapsePanelProps> = (props) => {
  const { t } = useTranslation()
  const [searchType, setSearchType] = React.useState<
    ResourceType.PROJECT | ResourceType.PORTFOLIO | ResourceType.TEAM | ResourceType.CUSTOMER
  >(0)

  const { data, loading } = useRequest<Favorite[]>(
    {
      url: '/api/project/getMyFavourites',
      params: {
        ...(() => {
          switch (searchType) {
            case ResourceType.PROJECT:
              return { type: searchType }
            case ResourceType.PORTFOLIO:
              return { type: searchType }
            case ResourceType.TEAM:
              return { type: searchType }
            case ResourceType.CUSTOMER:
              return { type: searchType }
          }
        })(),
      },
    },
    {
      refreshDeps: [searchType],
    }
  )

  const list = data

  const options = [
    { label: t('project.projects'), value: ResourceType.PROJECT, name: '项目' },
    { label: t('portfolio.portfolios'), value: ResourceType.PORTFOLIO, name: '组合' },
    { label: t('teams'), value: ResourceType.TEAM, name: '团队' },
    { label: t('customer.title'), value: ResourceType.CUSTOMER, name: '客户项目' },
  ]

  const onChange = (
    searchType: ResourceType.PROJECT | ResourceType.PORTFOLIO | ResourceType.TEAM | ResourceType.CUSTOMER
  ) => {
    setSearchType(searchType)
  }

  const header = (
    <>
      <span>{props.header}</span>
      <Switch options={options} value={searchType} onChange={onChange} />
    </>
  )

  return (
    <Collapse.Panel {...props} header={header}>
      <List
        className='max-h-600px overflow-y-auto'
        itemLayout='horizontal'
        size='small'
        bordered
        loading={loading}
        locale={{
          emptyText: (
            <div className='text-left'>
              {t('personal.myfavorite.tip')}
              {options.find((o) => o.value === searchType)!.label}
            </div>
          ),
        }}
        dataSource={list || []}
        renderItem={(p) => {
          switch (p.type) {
            case ResourceType.PROJECT:
              return (
                <List.Item className='space-x-24px'>
                  <Text className='flex-grow flex items-stretch'>
                    <RiskBar className='mr-20px' risk={p.project.risk} />
                    <Link
                      to={routes.project.page(p.selfId)}
                      onClick={() => {
                        ga('event', traceEvent.personalPage.events.click_check_detail, {
                          event_category: traceEvent.personalPage.event_categories.ef_pmo_home_my_favorites_projects,
                          action: traceEvent.personalPage.action,
                          event_label: p.project.name,
                          target: traceEvent.personalPage.targets.ef_pmo_project_detail,
                        })
                        window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_home)
                      }}
                    >
                      {p.project.name}
                    </Link>
                  </Text>

                  <Favorite selfId={p.selfId} type={p.type} isFavourited={true} />

                  <ProjectProgress project={p.project} />

                  <PlanDate date={p.project.endDate} tooltip={t('plan end date')} />
                </List.Item>
              )
            case ResourceType.PORTFOLIO:
              return (
                <List.Item className='space-x-24px'>
                  <Text className='flex-grow flex items-stretch'>
                    <RiskBar className='mr-20px' risk={p.portfolio.risk} />
                    <Link
                      to={routes.portfolio.page(p.selfId)}
                      onClick={() => {
                        ga('event', traceEvent.personalPage.events.click_check_detail, {
                          event_category: traceEvent.personalPage.event_categories.ef_pmo_home_my_favorites_portfolios,
                          action: traceEvent.personalPage.action,
                          event_label: p.portfolio.name,
                          target: traceEvent.personalPage.targets.ef_pmo_portfolio_detail,
                        })
                      }}
                    >
                      {p.portfolio.name}
                    </Link>
                  </Text>

                  <Favorite selfId={p.selfId} type={p.type} isFavourited={true} />

                  <PlanDate date={p.portfolio.endDate} tooltip={t('end')} />
                </List.Item>
              )
            case ResourceType.TEAM:
              return (
                <List.Item className='space-x-24px'>
                  <Text className='flex-grow flex items-stretch'>
                    <RiskBar className='mr-20px' risk={p.team.risk} />
                    <Link
                      to={routes.team.home({ departmentId: p.team.departmentId })}
                      onClick={() => {
                        ga('event', traceEvent.personalPage.events.click_check_detail, {
                          event_category: traceEvent.personalPage.event_categories.ef_pmo_home_my_favorites_teams,
                          action: traceEvent.personalPage.action,
                          event_label: p.team.name,
                          target: traceEvent.personalPage.targets.ef_pmo_teams,
                        })
                      }}
                    >
                      {p.team.name}
                    </Link>
                  </Text>

                  <Favorite selfId={p.selfId} type={p.type} isFavourited={true} />

                  <PlanDate date={p.team.endDate} tooltip={t('end')} />
                </List.Item>
              )
            case ResourceType.CUSTOMER: {
              const customer = p.customer
              return (
                <List.Item className='space-x-24px'>
                  <Text className='flex-grow flex items-stretch'>
                    <RiskBar className='mr-20px' risk={customer.risk} />
                    <Link
                      onClick={() => {
                        ga('event', traceEvent.personalPage.events.click_check_detail, {
                          event_category: traceEvent.personalPage.event_categories.ef_pmo_home_my_favorites_customers,
                          action: traceEvent.personalPage.action,
                          event_label: customer.name,
                          target: traceEvent.personalPage.targets.ef_pmo_customer_detail,
                        })
                        window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_home)
                      }}
                      to={routes.customer.detail(p.selfId)}
                    >
                      {customer.name}
                    </Link>
                  </Text>
                  <Favorite selfId={p.selfId} type={p.type} isFavourited={true} />
                  <ProjectProgress project={customer as any} />
                  <PlanDate date={customer.endDate as any} tooltip={t('plan end date')} />
                </List.Item>
              )
            }
          }
        }}
      />
    </Collapse.Panel>
  )
}
